import { CustomAccordionExpandIcon } from '@aibees/elephant/components/custom-accordion-expand-icon';
import { useSearchQueryFilter } from '@aibees/elephant/hooks';
import { Icon } from '@aibees/shared/components';
import { classnames } from '@aibees/shared/utils';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Badge,
  Box,
  Typography,
} from '@mui/material';
import { memo, useMemo, useState } from 'react';
import { FilterSection } from '../../components/filter-section';
import { useFilterSections } from '../../hooks/use-filter-sections';
import { ChildrenFilter } from '../children-filter';
import * as SX from './sx';
import type { AccordionSectionFilterProps as Props } from './types';

const ACCORDION_SLOT_PROPS = {
  transition: { unmountOnExit: true },
};

export const AccordionSectionFilter = memo(
  ({ className, ...filter }: Props) => {
    const { mainItems, moreItems } = useFilterSections(filter);
    const selectKeys = useMemo(
      () => filter.items?.map((i) => i.field!).filter(Boolean) ?? [],
      [filter.items]
    );
    const { getParam } = useSearchQueryFilter({ select: selectKeys });
    const [open, setOpen] = useState<boolean>(false);

    const isAnyOfItemsSet = useMemo(() => {
      return !!filter.items?.map((i) => getParam(i.field!)).filter(Boolean)
        .length;
    }, [filter.items, getParam]);

    const isAnyOfMoreItemsSet = useMemo(() => {
      return !!moreItems?.map((i) => getParam(i.field!)).filter(Boolean).length;
    }, [moreItems, getParam]);

    const handleChange = () => {
      setOpen((prev) => !prev);
    };

    return (
      <Accordion
        className={classnames('AccordionSectionFilter', className)}
        variant="filled"
        color="transparent"
        expanded={open}
        slotProps={ACCORDION_SLOT_PROPS}
      >
        <AccordionSummary
          expandIcon={<CustomAccordionExpandIcon open={open} size={14} />}
          sx={SX.summary}
          onClick={handleChange}
        >
          <Box display="flex" alignItems="center" gap={0.5}>
            {isAnyOfItemsSet && (
              <>
                <Badge color="primary" variant="dot" size={6} />
                <Box marginRight={0.2} />
              </>
            )}
            <Icon
              color={open ? 'primary' : 'grey.700'}
              size={16}
              name={filter.icon ?? 'setting-1'}
            />
            <Typography color={open ? 'primary' : 'grey.700'} variant="body4">
              {filter.name}
            </Typography>
          </Box>
        </AccordionSummary>

        <AccordionDetails sx={SX.details}>
          <Box display="flex" flexDirection="column" gap={1}>
            {mainItems?.map(({ key, ...item }) => (
              <ChildrenFilter key={key} open {...item} />
            ))}
            {moreItems?.length ? (
              <FilterSection open={isAnyOfMoreItemsSet} title="More Filters">
                {moreItems?.map(({ key, ...item }) => (
                  <ChildrenFilter key={key} open {...item} />
                ))}
              </FilterSection>
            ) : null}
          </Box>
        </AccordionDetails>
      </Accordion>
    );
  }
);

AccordionSectionFilter.displayName = 'AccordionSectionFilter';
